import { useVariablesDictionary } from "@babackapp/editor-blocks-library/variables-context";
import { Button } from "@babackapp/ui/components/button";
import { FloatingPanelContent, FloatingPanelHeader, FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@babackapp/ui/components/select";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useForm, useFormContext } from "react-hook-form";
import { toast } from "@babackapp/ui/components/sonner";
import { createValuesRecord } from "@babackapp/variables-registry";
import useSendTrackingTestEmail from "@babackapp/private-api-client/client/use-send-tracking-test-email";
import { useEmailEditorLocale } from "../context/templates-editor-locale-context";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { EmailTemplateFormData } from "../types";
import { useTranslation } from "react-i18next";
import { useTemplatesEditorSettings } from "../context/templates-editor-settings-context";

type SendTestEmailFormData = {
  preset: string;
  recipient: string;
};

type SendTestEmailPanelProps = {
  onClose: () => void;
};

export default function SendTestEmailPanel({ onClose }: SendTestEmailPanelProps) {
  const { t } = useTranslation("trackoffice");
  const settings = useTemplatesEditorSettings();

  const form = useForm<SendTestEmailFormData>({
    defaultValues: {
      preset: "default",
      recipient: settings.currentUserEmail ?? "",
    },
  });

  const { data: shop } = useCurrentShopSuspense();
  const { currentLocale } = useEmailEditorLocale();
  const { variables } = useVariablesDictionary();
  const { domain } = useTemplatesEditorConfig();
  const { mutateAsync: sendTestEmail, isPending: isSending } = useSendTrackingTestEmail(shop.id);
  const { getValues: getFormValues } = useFormContext<EmailTemplateFormData>();

  const handleSendTestEmail = async (data: SendTestEmailFormData) => {
    try {
      const formValues = getFormValues();

      // Get variable values from context
      const variableValues = createValuesRecord(variables);

      await sendTestEmail({
        requestBody: {
          locale: currentLocale,
          subject: formValues.subject?.[currentLocale] as string | undefined,
          templateData: formValues.content,
          variables: variableValues,
          contextPreset: data.preset,
          to: data.recipient,
          // Tells the backend which renderer to use (return vs tracking).
          domain,
        },
      });

      // Close panel on success
      onClose();
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  return (
    <div className="absolute inset-0 bg-card flex flex-col animate-in slide-in-from-bottom duration-300 rounded-md z-100">
      <FloatingPanelHeader>
        <button
          onClick={onClose}
          className="flex items-center gap-1 hover:opacity-70 transition-opacity"
          disabled={isSending}
        >
          <ChevronLeft className="size-4" />
          <span className="text-xs font-medium">
            {t("templatesEditor.sendTestEmail.cancel", "Annuler l'envoi")}
          </span>
        </button>
      </FloatingPanelHeader>
      <FloatingPanelSeparator />
      <FloatingPanelContent className="flex-1 overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSendTestEmail)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="preset"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-muted-foreground">
                    {t("templatesEditor.sendTestEmail.preset", "Preset")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSending}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="default">
                        {t("templatesEditor.sendTestEmail.presetDefault", "Default")}
                      </SelectItem>
                      <SelectItem value="pickup-point">
                        {t("templatesEditor.sendTestEmail.presetPickupPoint", "Pickup Point")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recipient"
              rules={{ required: true }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-muted-foreground">
                    {t("templatesEditor.sendTestEmail.recipientEmail", "Email destinataire")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={settings.currentUserEmail || t("templatesEditor.sendTestEmail.recipientPlaceholder", "Your email address")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-2 justify-end mt-2">
              <Button type="submit" disabled={isSending} size="sm">
                {isSending ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    {t("templatesEditor.sendTestEmail.sending", "Envoi...")}
                  </>
                ) : (
                  t("templatesEditor.sendTestEmail.send", "Envoyer")
                )}
              </Button>
            </div>
          </form>
        </Form>
      </FloatingPanelContent>
    </div>
  );
}
